import { type FC, type ReactElement, type ReactNode, useMemo } from 'react';
import { WrapItem, Wrap } from '@chakra-ui/react';
import { Filtering } from '../../QOrganisms';
import { useTableInstrumentationMaybe } from '../../QOrganisms/Table/Instrumentation/context';
import { QActiveFilter } from './ActiveFilter';

/**
 * Renders active filters in a row and handles their dismissal.
 *
 * Requires a user provider and filter provider.
 */
export const QActiveFiltersView: FC = () => {
  const { definitions, filters, removeFilter, setFilters } =
    Filtering.useFiltering();
  const instrumentation = useTableInstrumentationMaybe();

  const items = useMemo((): ReactNode => {
    const removeArrayItem = (key: string, value: unknown) => {
      const updatedArray = (filters[key].value as unknown[]).filter(
        (v) => v !== value,
      );

      instrumentation?.markInteraction('filter_remove');
      setFilters({
        [key]: updatedArray.length > 0 ? updatedArray : null,
      });
    };

    return Object.entries(filters).map(([key, filter]) => {
      if (Array.isArray(filter.value)) {
        return filter.value.map(
          (value, i): ReactElement => (
            <WrapItem key={`${key}-${i}`}>
              <QActiveFilter
                filter={{ ...filter, value }}
                onRemove={() => removeArrayItem(key, value)}
                render={definitions[key]?.activeRender}
              />
            </WrapItem>
          ),
        );
      }
      return (
        <WrapItem key={key}>
          <QActiveFilter
            filter={filter}
            onRemove={() => {
              instrumentation?.markInteraction('filter_remove');
              removeFilter(key);
            }}
            render={definitions[key]?.activeRender}
          />
        </WrapItem>
      );
    });
  }, [definitions, filters, removeFilter, instrumentation]);

  return (
    <Wrap direction="row" spacing="8px">
      {items}
    </Wrap>
  );
};
